<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { AtomText, AtomButton, useToast } from "@chatfood/design-system";
import { ref, watch } from "vue";
import type { IOrderForLaterWarnEmits } from "./emits";
import { useLocale } from "~/composables/use-locale";
import { SchedulePicker } from "~/components/schedule-picker";
import { useBasketStore } from "~/stores/basket";
import type { IDomainOrderScheduledTime } from "~/domain/order-scheduled-time";
import { useNavigation } from "~/composables/use-navigation";
import { useOutletStore } from "~/stores/outlet";
import { useBusinessStore } from "#imports";

const css = bemBuilder("order-for-later-warn");
const emits = defineEmits<IOrderForLaterWarnEmits>();
const business = useBusinessStore();
const basket = useBasketStore();
const outlet = useOutletStore();
const { t } = useLocale();
const { push, route } = useNavigation();
const { toastError } = useToast();

watch(
  () => route.value.fullPath,
  () => {
    emits("dismiss");
  },
);

const scheduledTime = ref<IDomainOrderScheduledTime>();
const isConfirming = ref(false);

const onSetSchedule = (value?: IDomainOrderScheduledTime) => {
  scheduledTime.value = value;
};

const onConfirm = async () => {
  isConfirming.value = true;

  try {
    if (!basket.isLoaded) {
      await basket.loadBasketWithLocation({
        businessId: business.id,
        currency: business.currency,
        outletId: basket.outletId,
        orderingMode: basket.orderingMode,
        salesChannel: business.salesChannel,
        location: basket.isForDelivery
          ? basket.pendingDeliveryLocation
          : undefined,
        scheduledTime: scheduledTime.value,
      });
    }
    await basket.setScheduledTime(scheduledTime.value);

    emits("dismiss");
  } catch {
    toastError(t("components.order_for_later_warn.confirm_error"));
  } finally {
    isConfirming.value = false;
  }
};

const onDismiss = () => {
  emits("dismiss");
  push("order", { orderingMode: basket.orderingMode });
};
</script>

<template>
  <div :class="css()">
    <div :class="css('message')">
      <AtomText type="h3">
        {{ t("components.order_for_later_warn.title") }}
      </AtomText>
      <AtomText color="color-text-secondary">
        {{
          t("components.order_for_later_warn.message", {
            outlet: outlet.name,
          })
        }}
      </AtomText>
    </div>
    <SchedulePicker
      :outlet-id="basket.outletId"
      :ordering-mode="basket.orderingMode"
      :sales-channel="business.salesChannel"
      show-label
      @select="onSetSchedule"
    />
    <div :class="css('ctas')">
      <AtomButton
        size="large"
        :is-loading="isConfirming"
        :is-disabled="!scheduledTime"
        @click.prevent="onConfirm"
      >
        {{ t("components.order_for_later_warn.confirm_cta") }}
      </AtomButton>
      <AtomButton size="large" variant="secondary" @click.prevent="onDismiss">
        {{ t("components.order_for_later_warn.dismiss_cta") }}
      </AtomButton>
    </div>
  </div>
</template>

<style lang="scss">
.order-for-later-warn {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);

  &__message {
    text-align: center;
    padding-bottom: var(--spacing-lg);
    border-bottom: 1px solid var(--color-base-95);
  }

  &__ctas {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }
}
</style>
